import { trackActivationOnce } from 'api/similarityActivationMetric';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { generateThumbnail } from 'libs/e1api/design';
import { useE2Client } from 'modules/E2ClientProvider';
import { ReactNode, createContext, useEffect, useReducer, useRef } from 'react';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { isStandalone } from 'utils/environment';

import { selectOriginalEditorId } from '../editorSettingsSelector';
import { useHistory } from '../history/historyManager';
import { useAppSelector } from '../hooks';
import { selectInfograph } from '../infographSelector';

/**
 * An internal state to keep tracking of current editor saving status
 */
interface PersistState {
  /**
   * A flag indicated PersistState in saving state
   */
  saving: boolean;

  /**
   * Last saving error
   */
  lastError: string;

  /**
   * Last modified time
   */
  lastModified: Date | undefined;
}

interface PersistOptions {
  /**
   * Debounce time for persist call
   */
  delay: number;

  /**
   * An optional callback triggers after persists
   */
  callback?: () => void;
}

/**
 * Initial persist state
 */
const initState: PersistState = {
  saving: false,
  lastError: '',
  lastModified: undefined,
};

/**
 * Action type for persist reducer
 */
export enum ActionType {
  SAVING = 'saving',
  FINISHED = 'finished',
  ERROR = 'error',
}

/**
 * @typeParam T - Type of string in {@link ActionType}
 * @typeParam P - Type of generic object literal as payload
 */
type PersistAction<T extends string = string, P = any> = {
  type: T;
  payload?: P;
};

/**
 * This reducer is used of manipulated persist state
 *
 * @param state - The persist state
 * @param action - The reducer action
 * @returns The persist state
 */
function persistReducer(state: PersistState, action: PersistAction): PersistState {
  switch (action.type) {
    case ActionType.SAVING:
      return { ...state, saving: true, lastError: '' };
    case ActionType.FINISHED:
      return { ...state, saving: false, lastError: '', lastModified: new Date() };
    case ActionType.ERROR:
      return { ...state, saving: false, lastError: action.payload.error };
    default:
      throw new Error(`Invalid Persist Action [${action.type}]`);
  }
}

/**
 * A persist manager hook returns current persist state
 *
 * @see {@link https://github.com/microsoft/TypeScript/issues/27942} tsdoc has issued with destructured param
 *
 * @param options - Persist options
 * @param options.delay - Debounce save time
 * @returns The persist state
 */
export function usePersistManager({ delay }: PersistOptions): PersistState {
  const { venngageE2 } = useVenngageE1Gate();
  const { getStore } = useHistory();
  const [state, dispatch] = useReducer(persistReducer, initState);
  const infographic = useAppSelector(selectInfograph);
  const debouncePatch = useDebouncedCallback(patchInfographic, delay);
  const { client, isReadonly } = useE2Client();

  const originalEditorId = useAppSelector(selectOriginalEditorId);
  const isLoaded = useRef(false);

  // Flag to prevent multiple save request while pending
  // This is for the issue that useEffect is called debounceSave multiple times before the first save request is finished
  // Update the checksum after the first request has completed, and call debounceSave again if it added another pendingDebounceSave in the useEffect
  const isPendingRequest = useRef(false);
  const pendingDebounceSave = useRef<(() => void) | null>(null);

  async function patchInfographic(infographicData: InfographState) {
    try {
      isPendingRequest.current = true;
      dispatch({ type: ActionType.SAVING });

      // TODO: Remove this when we have proper permission management in the E2 Backend
      if (isReadonly) throw new Error('Cannot save readonly infographic');

      const unsavedPatches = getStore()?.unsavedPatches;
      if (!unsavedPatches) throw new Error('No unsaved patches found');

      // Copy unsaved patches array to prevent to modified while the API is pending
      const pendingUnsavedPatches = [...unsavedPatches];

      await client?.commitUnsavedPatches(infographicData, pendingUnsavedPatches);

      // Remove all saved patches
      getStore()?.removeUnsavedPatches(pendingUnsavedPatches);

      // If another debounceSave call occurs while the API is pending, call it again
      // pendingDebounceSave is using the debounceSave function wrapped by anonymous function
      if (pendingDebounceSave.current !== null) {
        pendingDebounceSave.current();
        pendingDebounceSave.current = null;
      }

      isPendingRequest.current = false;
      dispatch({ type: ActionType.FINISHED });

      // Generate the new thumbnail
      // DO NOT allow to use the thumbnail API in standalone mode
      if (originalEditorId && !isStandalone) {
        generateThumbnail(originalEditorId, infographicData.size);

        /**
         * Trigger activation tracking event on save
         * DO NOT use await to prevent blocking the save process
         */
        if (venngageE2?.experiments?.enableActivationTracking) {
          trackActivationOnce(originalEditorId, () =>
            pendingUnsavedPatches.some((patch) => patch?.path.includes(WidgetType.Text)),
          );
        }
      }
    } catch (err: any) {
      dispatch({ type: ActionType.ERROR, payload: { error: err?.message } });
      isPendingRequest.current = false;
      pendingDebounceSave.current = null;
    }
  }

  // Trigger useEffect when infographic changes
  useEffect(() => {
    // Prevent to save on first load
    const isFirstLoad = !!(!isLoaded.current && originalEditorId);
    if (isFirstLoad) {
      isLoaded.current = isFirstLoad;
      return;
    }

    // Prevent patches if data is not loaded
    if (!isLoaded.current) return;

    if (isPendingRequest.current) {
      pendingDebounceSave.current = () => debouncePatch(infographic);
    } else {
      debouncePatch(infographic);
    }

    return () => {
      debouncePatch.cancel();
    };
  }, [infographic, debouncePatch, originalEditorId]);

  return { ...state };
}

/**
 * A public persist manager context uses to keep tracking persist state
 */
export const PersistManagerContext = createContext<PersistState>(initState);

/**
 * Default debounce delay constant
 */
const DEFAULT_DEBOUNCE_DELAY = 1000;

/**
 * Return the persist manager provider component
 *
 * @param props - The provider props
 * @param props.children - A ReactNode
 * @returns The persist manager provider
 */
export const PersistManagerProvider = ({ children }: { children?: ReactNode }) => {
  const state = usePersistManager({ delay: DEFAULT_DEBOUNCE_DELAY });
  return <PersistManagerContext.Provider value={state}>{children}</PersistManagerContext.Provider>;
};
